import { FlexBox, Spinner } from '@dixa/lib-shared-habla';
import {
  getWidgetDefinitions,
  PartnerIntegrations,
  ShowNotificationFunctionProps,
  usePartnerIntegrationWidgetConfigurations,
} from 'lib-integrations-partner-widgets';
import { useHasAccess } from 'lib-shared-access-control';
import { config } from 'lib-shared-backend-config';
import {
  MetaConversation,
  NotificationPayloadT,
} from 'lib-shared-legacy-types';
import { ampli } from 'lib-shared-tracking';
import type { ElevionNode } from 'lib-shared-types-api';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { notificationActions } from 'components/notifications/actions/notification';
import { useLazyCustomFields } from 'utils/customFields/useLazyCustomFields';
import { useMergedCustomFieldAttributes } from 'utils/customFields/useMergedCustomFieldAttributes';
import { useFeatureFlag } from 'utils/featureFlagging/useFeatureFlag';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useServerStorage } from 'utils/useServerStorage';

import { CustomCards } from '../../cards/customCards/customCards';
import { useCardSectionVisibility } from '../../hooks/useCardSectionVisibility';
import { useCustomizationSnapshot } from '../../hooks/useCustomizationSnapshot';
import { useSidebarMode } from '../../hooks/useSidebarMode';
import { partnerIntegrationNotificationTimeout } from '../../partnerIntegrations';
import { reorderNumeric } from '../../utils/reorder';
import { CardMoveHandle } from '../cardMoveHandle/cardMoveHandle';
import { CardSection } from '../cardSection/cardSection';
import { VisibilityToggle } from '../visibilityToggle/visibilityToggle';

import {
  buildElevionConversationContext,
  buildRenderConditionMatchMap,
  collectMatchedFieldLabels,
} from '../../utils/conditions';
import { ConditionMatchedIndicator } from './components/conditionMatchedIndicator/conditionMatchedIndicator';
import { IntegrationsCardSummary } from './components/integrationCardSummary/integrationCardSummary';

type IntegrationCardsProps = {
  metaConversation: MetaConversation;
  isMinimized: boolean;
  isCardOpen: (card: string) => boolean;
  toggleCard: (card: string) => void;
  index: number;
};

const type = Symbol('nativeIntegrationCard');

const widgetDefinitions = getWidgetDefinitions();

export const IntegrationCards = ({
  metaConversation,
  isMinimized,
  isCardOpen,
  toggleCard,
  index,
}: IntegrationCardsProps): JSX.Element | null => {
  const { hiddenItems } = useCardSectionVisibility();
  const sidebarMode = useSidebarMode();

  const dispatch = useAppDispatch();
  const flags = useFeatureFlag([
    'partner-integrations-sidebar-conditions',
    'conversation-right-sidebar-visibility',
  ]);
  const conditionsEnabled = flags.partnerIntegrationsSidebarConditions;

  const hasIntegrationAccess = useHasAccess('conversation.integration');

  const storage = useServerStorage('user');

  const [forceShowHidden, setForceShowHidden] = useState(false);

  // Skip the partner-integrations query for users without the
  // `conversation.integration` scope: the gateway authorization-check plugin
  // would reject it and the log noise adds up. The early return below still
  // hides the section entirely for those users.
  const { widgets, isLoading } = usePartnerIntegrationWidgetConfigurations(
    config.url.partnerIntegrations,
    {
      skip: !hasIntegrationAccess,
    },
  );

  const conversationId = metaConversation.conversation?.id;
  const requesterId = metaConversation.requester?.id;
  const anyRenderConditions = useMemo(
    () =>
      conditionsEnabled &&
      (widgets ?? []).some(
        (widget) => (widget.renderConditions ?? []).length > 0,
      ),
    [widgets, conditionsEnabled],
  );

  const [
    readConversationCustomFields,
    { customFields: conversationCustomFields },
  ] = useLazyCustomFields();
  const [readContactCustomFields, { customFields: contactCustomFields }] =
    useLazyCustomFields();

  useEffect(() => {
    if (anyRenderConditions && conversationId) {
      readConversationCustomFields({
        entityId: 'conversation',
        entityInstanceId: `${conversationId}`,
      });
    }
  }, [anyRenderConditions, conversationId, readConversationCustomFields]);

  useEffect(() => {
    if (anyRenderConditions && requesterId) {
      readContactCustomFields({
        entityId: 'contact',
        entityInstanceId: requesterId,
      });
    }
  }, [anyRenderConditions, requesterId, readContactCustomFields]);

  const customAttributes = useMergedCustomFieldAttributes({
    skip: !anyRenderConditions,
  });

  const evalContext = useMemo(
    () =>
      buildElevionConversationContext({
        queueId: metaConversation.conversation?.queue?.id ?? null,
        tags: metaConversation.conversation?.tags ?? null,
        conversationCustomFields:
          anyRenderConditions && conversationId ? conversationCustomFields : [],
        contactCustomFields:
          anyRenderConditions && requesterId ? contactCustomFields : [],
      }),
    [
      metaConversation.conversation?.queue?.id,
      metaConversation.conversation?.tags,
      conversationCustomFields,
      contactCustomFields,
      anyRenderConditions,
      conversationId,
      requesterId,
    ],
  );

  const eligibleWidgets = useMemo(
    () =>
      (widgets ?? []).filter(
        (widget) =>
          widget.type in widgetDefinitions &&
          widget.enabled &&
          (sidebarMode === 'customization' ||
            !hiddenItems.has(widget.integrationKey)),
      ),
    [widgets, sidebarMode, hiddenItems],
  );

  // Pre-compute match state once per widget so sorting + the per-card
  // indicator can read from the same source instead of re-evaluating. When
  // the feature flag is off, the map stays empty so every widget falls back
  // to "matched" (preserves pre-feature behavior).
  const matchByWidgetId = useMemo(
    () =>
      conditionsEnabled
        ? buildRenderConditionMatchMap(eligibleWidgets, evalContext)
        : new Map<string, boolean>(),
    [conditionsEnabled, eligibleWidgets, evalContext],
  );

  const isWidgetMatched = useCallback(
    (widgetId: string): boolean => matchByWidgetId.get(widgetId) ?? true,
    [matchByWidgetId],
  );

  const hiddenByConditions = useMemo(() => {
    if (sidebarMode === 'customization') {
      return [];
    }

    return eligibleWidgets.filter(
      (widget) => matchByWidgetId.get(widget.id) === false,
    );
  }, [eligibleWidgets, matchByWidgetId, sidebarMode]);

  const matchedCount = useMemo(() => {
    const withConditions = eligibleWidgets.filter(
      (widget) => (widget.renderConditions ?? []).length > 0,
    );

    return withConditions.length - hiddenByConditions.length;
  }, [eligibleWidgets, hiddenByConditions]);

  const sortedWidgets = useMemo(() => {
    const showAll = sidebarMode === 'customization' || forceShowHidden;
    const base = showAll
      ? eligibleWidgets
      : eligibleWidgets.filter((widget) => isWidgetMatched(widget.id));

    const cardOrder = storage.data.conversation?.sidebarIntegrationOrder ?? {};

    return [...base].sort(
      (a, b) =>
        (cardOrder[a.id] ?? -Infinity) - (cardOrder[b.id] ?? -Infinity) ||
        a.name.localeCompare(b.name),
    );
  }, [
    eligibleWidgets,
    isWidgetMatched,
    forceShowHidden,
    sidebarMode,
    storage.data.conversation?.sidebarIntegrationOrder,
  ]);

  const { items: preparedWidgets, reorder: reorderWidgets } =
    useCustomizationSnapshot(sortedWidgets, 'integrationKey');

  const showNotification = useCallback(
    ({ title, text, icon, severity }: ShowNotificationFunctionProps) => {
      const time =
        severity === 'warning' || severity === 'danger'
          ? 0
          : partnerIntegrationNotificationTimeout;
      const className = severity as string;

      const payload: NotificationPayloadT = {
        title,
        text,
        icon,
        className,
        time,
      };
      dispatch(notificationActions.notificationAdd(payload));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const renderConditionMatchedIndicator = useCallback(
    (renderConditions: ElevionNode[], widgetId: string) => (
      <ConditionMatchedIndicator
        fieldLabels={collectMatchedFieldLabels(
          renderConditions,
          customAttributes,
        )}
        isMatched={isWidgetMatched(widgetId)}
      />
    ),
    [customAttributes, isWidgetMatched],
  );

  const handleMove = useCallback(
    ({ dragIndex, hoverIndex }: { dragIndex: number; hoverIndex: number }) => {
      reorderWidgets(dragIndex, hoverIndex);

      storage.set({
        conversation: (draft) => {
          draft.sidebarIntegrationOrder = reorderNumeric(
            preparedWidgets,
            dragIndex,
            hoverIndex,
          );
        },
      });

      ampli.conversationRightSidebarItemReordered({
        sidebarItemType: 'card',
      });
    },
    [storage, preparedWidgets, reorderWidgets],
  );

  if (!hasIntegrationAccess) {
    return null;
  }

  if (
    flags.conversationRightSidebarVisibility &&
    sidebarMode !== 'customization' &&
    !isLoading &&
    !preparedWidgets.length &&
    hiddenByConditions.length === 0
  ) {
    return null;
  }

  const sectionId = flags.conversationRightSidebarVisibility
    ? 'nativeIntegration'
    : 'legacyIntegration';

  return (
    <CardSection sectionId={sectionId} index={index} isMinimized={isMinimized}>
      {isLoading && (
        <FlexBox justifyContent="center" padding="400">
          <Spinner />
        </FlexBox>
      )}

      {conditionsEnabled && !isLoading && sidebarMode !== 'customization' && (
        <IntegrationsCardSummary
          totalCount={eligibleWidgets.length}
          matchedCount={matchedCount}
          hiddenWidgets={hiddenByConditions}
          isForceShown={forceShowHidden}
          onToggleForceShow={() => setForceShowHidden((current) => !current)}
        />
      )}

      <PartnerIntegrations
        configuration={{
          baseUrl: config.url.partnerIntegrations,
        }}
        widgets={preparedWidgets}
        conversationContext={{
          conversationId: metaConversation.conversation?.id || null,
          agent: metaConversation?.conversation?.assignment?.agent ?? undefined,
          customer: metaConversation?.requester ?? undefined,
        }}
        showNotification={showNotification}
        onToggleFn={toggleCard}
        isOpenFn={isCardOpen}
        isMinimized={isMinimized}
        renderDragElement={(key, idx, row) => (
          <CardMoveHandle
            visibilityCardId={key}
            index={idx}
            type={type}
            target={row}
            onMove={handleMove}
          />
        )}
        renderVisibilityToggleElement={(key: string) => (
          <VisibilityToggle itemType="card" itemId={key} />
        )}
        renderConditionMatchedIndicator={
          conditionsEnabled ? renderConditionMatchedIndicator : undefined
        }
        widgetAnnotations={conditionsEnabled ? matchByWidgetId : undefined}
      />

      {!flags.conversationRightSidebarVisibility && (
        <CustomCards
          isMinimized={isMinimized}
          csid={metaConversation.conversation?.id}
        />
      )}
    </CardSection>
  );
};
